import ChatActionsModelMC from '../../presentation/models/inbox/ChatActionsModelMC';
import { updateOrInsertConversationOptimized } from '../../actions/inbox/conversationOptimizedActions';
import { getCustomerData, getCustomerOrderCount } from '../../actions/inbox/customerDataActions';
import { LOAD_DATA_FAILURE } from '../../actions/inbox/conversationActions';
import buildCreateCustomerPayloadFromConversation from './buildCreateCustomerPayloadFromConversation';

const inflightByChatId = new Map();

function customerMatchesStore(getState, customerId) {
  const { customer } = getState().inboxRightReducer;
  return customer && String(customer.id) === String(customerId) ? customer : null;
}

/**
 * Ensures the current chat has a CRM customer: returns existing id or creates via API, updates Redux, loads customer sidebar data.
 *
 * @param {import('../../presentation/utils/conversationUtils').SerializedConversation} conversation
 * @returns {import('redux-thunk').ThunkAction<Promise<{ success: boolean, customerId?: *, customer?: *, error?: string }>, any, any, any>}
 */
const ensureInboxCustomer = (conversation) => async (dispatch, getState) => {
  if (!conversation?.id) {
    const error = 'Missing conversation';
    dispatch({ type: LOAD_DATA_FAILURE, error });
    return { success: false, error };
  }

  const chatKey = String(conversation.id);

  if (conversation.customerId) {
    const cid = conversation.customerId;
    const cached = customerMatchesStore(getState, cid);
    if (cached) {
      return { success: true, customerId: cid, customer: cached };
    }
    await dispatch(getCustomerData(cid));
    await dispatch(getCustomerOrderCount(cid));
    return { success: true, customerId: cid, customer: getState().inboxRightReducer.customer };
  }

  if (inflightByChatId.has(chatKey)) {
    return inflightByChatId.get(chatKey);
  }

  const promise = (async () => {
    try {
      const retailerId = getState().retailerUsersReducer?.retailer_info?.id;
      const payload = buildCreateCustomerPayloadFromConversation(conversation, { retailerId });
      if (!payload) {
        const error = 'Invalid conversation for create customer';
        dispatch({ type: LOAD_DATA_FAILURE, error });
        return { success: false, error };
      }

      const data = await ChatActionsModelMC.createCustomer(payload);
      const customerId = data.customer_id;

      await dispatch(updateOrInsertConversationOptimized({
        id: conversation.id,
        changes: { customer_id: customerId }
      }));

      await dispatch(getCustomerData(customerId));
      await dispatch(getCustomerOrderCount(customerId));

      const { customer } = getState().inboxRightReducer;
      return { success: true, customerId, customer };
    } catch (err) {
      const error = err?.message || 'Could not create customer';
      dispatch({ type: LOAD_DATA_FAILURE, error });
      return { success: false, error };
    } finally {
      inflightByChatId.delete(chatKey);
    }
  })();

  inflightByChatId.set(chatKey, promise);
  return promise;
};

export default ensureInboxCustomer;
